import React from 'react';
import Comment from './Comment';
import CrosspostCard from 'components/Common/CrosspostCard';
import { Comment as CommentData, Entity } from 'util/constants';
import { PrivacyLevel } from 'util/userUtils';
import { CoauthorSuggestion } from 'components/Coauthors/CoauthorSelect';
import type { PollDraft } from 'types/poll';

interface RenderCommentsProps {
  comments: CommentData[];
  depth?: number;
  maxDepth: number;
  isAllCollapsed: boolean;
  onAddReply: (
    parentId: number,
    content: string,
    privacy: PrivacyLevel,
    coauthors: CoauthorSuggestion[],
    mediaFiles?: FileList | null,
    pollDraft?: PollDraft | null
  ) => void;
  onUpdateComment: (
    id: number,
    content: string,
    privacy: PrivacyLevel,
    coauthors: CoauthorSuggestion[],
    addedCoauthors: CoauthorSuggestion[],
    removedCoauthors: CoauthorSuggestion[]
  ) => void;
  onDeleteComment: (id: number) => void;
  commentable_type: CommentData['commentable_type'];
  commentable_id: CommentData['commentable_id'];
  entity?: Entity;
  isEntitySection: boolean;
  showEntityCompactCard?: boolean;
}

const RenderComments: React.FC<RenderCommentsProps> = ({
  comments,
  depth = 0,
  maxDepth = 30,
  isAllCollapsed,
  onAddReply,
  onUpdateComment,
  onDeleteComment,
  commentable_type,
  commentable_id,
  isEntitySection,
  showEntityCompactCard = true, // Default to true
}) => {
  // Helper function to check if comment has meaningful content
  const hasContent = (content: string | undefined | null): boolean => {
    if (!content) return false;
    const trimmedContent = content.trim();
    return trimmedContent !== '' && trimmedContent !== '<p></p>';
  };

  return (
    <div className="comments-container card-description eoe-dark bg-transparent">
      {comments.map((comment) => {
        // Check if this is a crosspost with content
        const isCrosspost = comment.properties?.is_cross_post === true;
        const commentHasContent = hasContent(comment.content) || comment.properties?.has_poll === true;
        
        // For crosspost comments, always render CrosspostCard (it handles content logic internally)
        if (isCrosspost) {
          return (
            <CrosspostCard
              key={comment.id}
              crosspostComment={comment}
            />
          );
        }
        
        // For regular comments, only render if there's content
        if (!commentHasContent) {
          return null;
        }
        
        // Resolve the commentable target for THIS comment. Each row
        // carries its own `commentable_type`/`commentable_id` (from the
        // `comment_det` view). On a single-surface thread those equal
        // the section props, so the historical override was a harmless
        // denormalization. But the issue Discussion now inlines
        // teabate-argument rows cross-surface: an argument genuinely
        // belongs to a teabate (`commentable_type='teabate'`,
        // `commentable_id=<teabate id>`) even though it renders under
        // the issue section. Clobbering those with the section's
        // issue id is exactly why "Rebut" was POSTing the issue id as
        // the teabate id. Respect the comment's own target and only
        // fall back to the section props when the row omits them.
        const resolvedCommentableType = comment.commentable_type ?? commentable_type;
        const resolvedCommentableId = comment.commentable_id ?? commentable_id;

        // Cross-surface root argument detection. A teabate argument
        // (`kind === 'argument'`, `commentable_type === 'teabate'`)
        // can render in two places:
        //   1. Its own teabate profile Discussion — here the section
        //      IS the teabate, so the claim/title is already on the
        //      page and no extra context header is needed.
        //   2. The issue Discussion, where arguments are inlined
        //      cross-surface. Here the reader has NO context for which
        //      teabate claim the argument is about, so we surface the
        //      teabate-context header (claim/title + author) via
        //      `showCommentHeader`.
        // We distinguish the two by comparing the argument's own
        // commentable_type against the SECTION's commentable_type
        // (the `commentable_type` prop passed into RenderComments).
        // Only the ROOT argument (`level === 0`) gets the header;
        // nested rebuttals stay plain.
        const argumentLevel =
          typeof comment.properties?.level === 'number' ? comment.properties.level : 0;
        const isCrossSurfaceRootArgument =
          comment.kind === 'argument' &&
          comment.commentable_type === 'teabate' &&
          commentable_type !== 'teabate' &&
          argumentLevel === 0;

        // Render regular Comment component
        return (
          <Comment
            key={comment.id}
            {...comment}
            depth={depth}
            maxDepth={maxDepth}
            isAllCollapsed={isAllCollapsed}
            onAddReply={onAddReply}
            onUpdateComment={onUpdateComment}
            onDeleteComment={onDeleteComment}
            commentable_type={resolvedCommentableType}
            commentable_id={resolvedCommentableId}
            isEntitySection={isEntitySection}
            showEntityCompactCard={showEntityCompactCard}
            showCommentHeader={isCrossSurfaceRootArgument}
          />
        );
      })}
    </div>
  );
};

export default RenderComments;